import { useEffect } from "react";
import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/admin/ProtectedRoute";

import Header from "./components/Header";
import Footer from "./components/Footer";

import Home from "./pages/Home";

import VisionMissionMandate from "./pages/about/VisionMissionMandate";
import OGFZAHistory from "./pages/about/OGFZAHistory";
import TrackRecord from "./pages/about/TrackRecord";
import WhyOGFZ from "./pages/about/WhyOGFZ";
import ManagementTeam from "./pages/about/ManagementTeam";
import OGFZAOrganogram from "./pages/about/OGFZAOrganogram";
import Legal from "./pages/about/Legal";
import MDMessage from "./pages/about/MDMessage";
import Incentives from "./pages/about/Incentives";
import ClientOverview from "./pages/about/ClientOverview";

import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import SiteTerms from "./pages/legal/SiteTerms";
import Disclaimer from "./pages/legal/Disclaimer";

import BlogPage from "./pages/blog/BlogPage";
import BlogSinglePage from "./pages/blog/BlogSinglePage";

import AboutSPV from "./pages/spv/AboutSPV";
import ContactSPV from "./pages/spv/ContactSPV";

import RegistrationProcedure from "./pages/free-zones/RegistrationProcedure";
import Onne from "./pages/free-zones/Onne";
import NotoreIndustrialCity from "./pages/free-zones/NotoreIndustrialCity";
import Brawal from "./pages/free-zones/Brawal";
import BESTAF from "./pages/free-zones/BESTAF";
import EkoSupport from "./pages/free-zones/EkoSupport";
import Liberty from "./pages/free-zones/Liberty";
import SPFLFZE from "./pages/free-zones/SPFLFZE";
import Brass from "./pages/free-zones/Brass";
import Warri from "./pages/free-zones/Warri";
import SecuredBunkeringAnchorage from "./pages/free-zones/SecuredBunkeringAnchorage";
import OrashiSpecialEnergy from "./pages/free-zones/OrashiSpecialEnergy";

import Registry from "./pages/services/registry/Registry";
import Engineering from "./pages/services/engineering/Engineering";
import HSE from "./pages/services/hse/HSE";
import Operations from "./pages/services/operations/Operations";

import Login from "./pages/portal/Login";
import RegistryPortal from "./pages/portal/RegistryPortal";
import EngineeringPortal from "./pages/portal/EngineeringPortal";
import HSEPortal from "./pages/portal/HSEPortal";
import OperationsPortal from "./pages/portal/OperationsPortal";
import ProtectedPortalRoute from "./pages/portal/ProtectedPortalRoute";

import Newsletters from "./pages/publications/Newsletters";
import OGFZADownload from "./pages/publications/OGFZADownload";
import EaseOfBusinessServiceCharter from "./pages/publications/EaseOfBusinessServiceCharter";
import MediaGallery from "./pages/publications/MediaGallery";
import Announcements from "./pages/publications/Announcements";
import AnnouncementSingle from "./pages/publications/AnnouncementSingle";

import OGFZAAct1996 from "./pages/downloads/OGFZAAct1996";
import OGFZARegulations2019 from "./pages/downloads/OGFZARegulations2019";
import FreeZoneApplicationForm from "./pages/downloads/FreeZoneApplicationForm";
import MemoAndArticlesOfAssociation from "./pages/downloads/MemoAndArticlesOfAssociation";
import ParticularsOfDirectors from "./pages/downloads/ParticularsOfDirectors";
import StatementOfShareCapital from "./pages/downloads/StatementOfShareCapital";
import OGFZABOIDisclosureForm from "./pages/downloads/OGFZABOIDisclosureForm";
import DisclosureOfBeneficialOwnersRegulation from "./pages/downloads/DisclosureOfBeneficialOwnersRegulation";
import ClientListDownload from "./pages/downloads/ClientListDownload";
import PublicFinancialRecords from "./pages/public-information/PublicFinancialRecords";

import FAQ from "./pages/contact-help/FAQ";
import ClientServiceLines from "./pages/contact-help/ClientServiceLines";
import CustomerService from "./pages/contact-help/CustomerService";
import ComplaintPage from "./pages/contact-help/ComplaintPage";
import TrackComplaint from "./pages/contact-help/TrackComplaint";
import ComplaintDataBankPage from "./pages/contact-help/ComplaintDataBankPage";

import AdminSigninPage from "./pages/admin/AdminSigninPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";

import AdminPosts from "./pages/admin/posts/AdminPosts";
import AdminAddPost from "./pages/admin/posts/AdminAddPost";
import AdminEditPost from "./pages/admin/posts/AdminEditPost";

import AdminCategories from "./pages/admin/categories/AdminCategories";
import AdminTags from "./pages/admin/tags/AdminTags";
import AdminComments from "./pages/admin/comments/AdminComments";
import AdminMedia from "./pages/admin/media/AdminMedia";
import AdminUsers from "./pages/admin/users/AdminUsers";
import AdminNewsletter from "./pages/admin/newsletter/AdminNewsletter";
import AdminContacts from "./pages/admin/contacts/AdminContacts";
import AdminSEOSettings from "./pages/admin/seo/AdminSEOSettings";

import AdminProjects from "./pages/admin/projects/AdminProjects";
import AdminAddProject from "./pages/admin/projects/AdminAddProject";
import AdminEditProject from "./pages/admin/projects/AdminEditProject";
import AdminProjectCategories from "./pages/admin/projects/AdminProjectCategories";

import AdminComplaints from "./pages/admin/complaints/AdminComplaints";
import AdminComplaintDetails from "./pages/admin/complaints/AdminComplaintDetails";
import AdminPartnerships from "./pages/admin/partnerships/AdminPartnerships";

import AdminAnnouncements from "./pages/admin/announcements/AdminAnnouncements";
import AdminAnnouncementForm from "./pages/admin/announcements/AdminAnnouncementForm";
import AdminAnnouncementCategories from "./pages/admin/announcements/AdminAnnouncementCategories";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const WebsiteLayout = ({ children }) => (
  <>
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </>
);

const PublicPage = ({ children }) => <WebsiteLayout>{children}</WebsiteLayout>;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              borderRadius: "14px",
              fontWeight: "600",
            },
          }}
        />

        <Routes>
          <Route
            path="/"
            element={
              <PublicPage>
                <Home />
              </PublicPage>
            }
          />

          <Route path="/about" element={<Navigate to="/about/vision-mission-mandate" replace />} />
          <Route path="/about/vision-mission-mandate" element={<PublicPage><VisionMissionMandate /></PublicPage>} />
          <Route path="/about/history" element={<PublicPage><OGFZAHistory /></PublicPage>} />
          <Route path="/about/track-record" element={<PublicPage><TrackRecord /></PublicPage>} />
          <Route path="/about/why-ogfz" element={<PublicPage><WhyOGFZ /></PublicPage>} />
          <Route path="/about/management-team" element={<PublicPage><ManagementTeam /></PublicPage>} />
          <Route path="/about/organogram" element={<PublicPage><OGFZAOrganogram /></PublicPage>} />
          <Route path="/about/legal" element={<PublicPage><Legal /></PublicPage>} />
          <Route path="/about/md-message" element={<PublicPage><MDMessage /></PublicPage>} />
          <Route path="/about/incentives" element={<PublicPage><Incentives /></PublicPage>} />
          <Route path="/about/client-overview" element={<PublicPage><ClientOverview /></PublicPage>} />

          <Route path="/spv" element={<Navigate to="/spv/about" replace />} />
          <Route path="/spv/about" element={<PublicPage><AboutSPV /></PublicPage>} />
          <Route path="/spv/contact" element={<PublicPage><ContactSPV /></PublicPage>} />

          <Route path="/free-zones" element={<Navigate to="/free-zones/registration-procedure" replace />} />
          <Route path="/free-zones/registration-procedure" element={<PublicPage><RegistrationProcedure /></PublicPage>} />
          <Route path="/free-zones/onne" element={<PublicPage><Onne /></PublicPage>} />
          <Route path="/free-zones/notore-industrial-city" element={<PublicPage><NotoreIndustrialCity /></PublicPage>} />
          <Route path="/free-zones/brawal" element={<PublicPage><Brawal /></PublicPage>} />
          <Route path="/free-zones/bestaf" element={<PublicPage><BESTAF /></PublicPage>} />
          <Route path="/free-zones/eko-support" element={<PublicPage><EkoSupport /></PublicPage>} />
          <Route path="/free-zones/liberty" element={<PublicPage><Liberty /></PublicPage>} />
          <Route path="/free-zones/spfl-fze" element={<PublicPage><SPFLFZE /></PublicPage>} />
          <Route path="/free-zones/brass" element={<PublicPage><Brass /></PublicPage>} />
          <Route path="/free-zones/warri" element={<PublicPage><Warri /></PublicPage>} />
          <Route path="/free-zones/sba" element={<PublicPage><SecuredBunkeringAnchorage /></PublicPage>} />
          <Route path="/free-zones/secured-bunkering-anchorage" element={<PublicPage><SecuredBunkeringAnchorage /></PublicPage>} />
          <Route path="/free-zones/orashi-special-energy" element={<PublicPage><OrashiSpecialEnergy /></PublicPage>} />

          <Route path="/services" element={<Navigate to="/services/registry" replace />} />
          <Route path="/services/registry" element={<PublicPage><Registry /></PublicPage>} />
          <Route path="/services/engineering" element={<PublicPage><Engineering /></PublicPage>} />
          <Route path="/services/hse" element={<PublicPage><HSE /></PublicPage>} />
          <Route path="/services/operations" element={<PublicPage><Operations /></PublicPage>} />

          <Route path="/portal" element={<Navigate to="/portal/login" replace />} />
          <Route path="/portal/login" element={<Login />} />

          <Route
            path="/portal/registry-portal"
            element={
              <ProtectedPortalRoute>
                <RegistryPortal />
              </ProtectedPortalRoute>
            }
          />

          <Route
            path="/portal/engineering-portal"
            element={
              <ProtectedPortalRoute>
                <EngineeringPortal />
              </ProtectedPortalRoute>
            }
          />

          <Route
            path="/portal/hse-portal"
            element={
              <ProtectedPortalRoute>
                <HSEPortal />
              </ProtectedPortalRoute>
            }
          />

          <Route
            path="/portal/operations-portal"
            element={
              <ProtectedPortalRoute>
                <OperationsPortal />
              </ProtectedPortalRoute>
            }
          />

          <Route path="/portal/fms" element={<Navigate to="/portal/registry-portal" replace />} />

          <Route path="/publications" element={<Navigate to="/publications/announcement" replace />} />
          <Route path="/publications/news" element={<PublicPage><BlogPage /></PublicPage>} />
          <Route path="/publications/news/:slug" element={<PublicPage><BlogSinglePage /></PublicPage>} />
          <Route path="/publications/announcement" element={<PublicPage><Announcements /></PublicPage>} />
          <Route path="/publications/announcements" element={<PublicPage><Announcements /></PublicPage>} />
          <Route path="/publications/announcements/:slug" element={<PublicPage><AnnouncementSingle /></PublicPage>} />
          <Route path="/publications/newsletters" element={<PublicPage><Newsletters /></PublicPage>} />
          <Route path="/publications/ogfza-download" element={<PublicPage><OGFZADownload /></PublicPage>} />
          <Route path="/publications/ease-of-business-service-charter" element={<PublicPage><EaseOfBusinessServiceCharter /></PublicPage>} />
          <Route path="/publications/media-gallery" element={<PublicPage><MediaGallery /></PublicPage>} />
            <Route
  path="/public-information/public-financial-records"
  element={
    <PublicPage>
      <PublicFinancialRecords />
    </PublicPage>
  }
/>
          <Route path="/downloads" element={<Navigate to="/downloads/ogfza-act-1996" replace />} />
          <Route path="/downloads/ogfza-act-1996" element={<PublicPage><OGFZAAct1996 /></PublicPage>} />
          <Route path="/downloads/ogfza-regulations-2019" element={<PublicPage><OGFZARegulations2019 /></PublicPage>} />
          <Route path="/downloads/free-zone-application-form" element={<PublicPage><FreeZoneApplicationForm /></PublicPage>} />
          <Route path="/downloads/memo-and-articles-of-association" element={<PublicPage><MemoAndArticlesOfAssociation /></PublicPage>} />
          <Route path="/downloads/particulars-of-directors" element={<PublicPage><ParticularsOfDirectors /></PublicPage>} />
          <Route path="/downloads/statement-of-share-capital" element={<PublicPage><StatementOfShareCapital /></PublicPage>} />
          <Route path="/downloads/ogfza-boi-disclosure-form" element={<PublicPage><OGFZABOIDisclosureForm /></PublicPage>} />
          <Route path="/downloads/disclosure-of-beneficial-owners-regulation" element={<PublicPage><DisclosureOfBeneficialOwnersRegulation /></PublicPage>} />
          <Route path="/downloads/client-list-download" element={<PublicPage><ClientListDownload /></PublicPage>} />

          <Route path="/contact" element={<Navigate to="/contact-help/faq" replace />} />
          <Route path="/contact-help" element={<Navigate to="/contact-help/faq" replace />} />
          <Route path="/contact/faq" element={<Navigate to="/contact-help/faq" replace />} />
          <Route path="/contact/client-service-lines" element={<Navigate to="/contact-help/client-service-lines" replace />} />
          <Route path="/contact/customer-service" element={<Navigate to="/contact-help/customer-service" replace />} />

          <Route path="/contact-help/faq" element={<PublicPage><FAQ /></PublicPage>} />
          <Route path="/contact-help/client-service-lines" element={<PublicPage><ClientServiceLines /></PublicPage>} />
          <Route path="/contact-help/customer-service" element={<PublicPage><CustomerService /></PublicPage>} />
          <Route path="/contact-help/complaints" element={<PublicPage><ComplaintPage /></PublicPage>} />
          <Route path="/contact-help/track-complaint" element={<PublicPage><TrackComplaint /></PublicPage>} />
          <Route path="/contact-help/complaint-data-bank" element={<PublicPage><ComplaintDataBankPage /></PublicPage>} />

          <Route path="/privacy" element={<PublicPage><PrivacyPolicy /></PublicPage>} />
          <Route path="/terms" element={<PublicPage><SiteTerms /></PublicPage>} />
          <Route path="/disclaimer" element={<PublicPage><Disclaimer /></PublicPage>} />

          <Route path="/admin-signin" element={<AdminSigninPage />} />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="posts" element={<AdminPosts />} />
            <Route path="posts/add" element={<AdminAddPost />} />
            <Route path="posts/edit/:id" element={<AdminEditPost />} />

            <Route path="categories" element={<AdminCategories />} />
            <Route path="tags" element={<AdminTags />} />
            <Route path="comments" element={<AdminComments />} />
            <Route path="media" element={<AdminMedia />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="seo" element={<AdminSEOSettings />} />

            <Route path="projects" element={<AdminProjects />} />
            <Route path="projects/add" element={<AdminAddProject />} />
            <Route path="projects/edit/:id" element={<AdminEditProject />} />
            <Route path="projects/categories" element={<AdminProjectCategories />} />

            <Route path="complaints" element={<AdminComplaints />} />
            <Route path="complaints/:id" element={<AdminComplaintDetails />} />

            <Route path="partnerships" element={<AdminPartnerships />} />

            <Route path="announcements" element={<AdminAnnouncements />} />
            <Route path="announcements/create" element={<AdminAnnouncementForm />} />
            <Route path="announcements/edit/:id" element={<AdminAnnouncementForm />} />
            <Route path="announcement-categories" element={<AdminAnnouncementCategories />} />
          </Route>

          <Route path="*" element={<Navigate to="/contact-help/faq" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;